import React from 'react';
import { useWindowDimensions } from 'react-native';
import type { LayoutRectangle, View } from 'react-native';

import { PopupPosition } from './popup.types';

export interface ExtendedLayoutRectangle extends LayoutRectangle {
  pageX: number;
  pageY: number;
}

interface UsePopupArgs {
  isVisible: boolean;
  popupPosition: PopupPosition;
  popupSpacing: number;
  childrenContainerRef: React.RefObject<View>;
}

export function usePopup({
  isVisible,
  popupPosition,
  childrenContainerRef,
}: UsePopupArgs) {
  const { width, height } = useWindowDimensions();

  const [childrenRect, setChildrenRect] =
    React.useState<ExtendedLayoutRectangle>();

  const finalPopupPosition = React.useMemo(() => {
    if (popupPosition !== 'auto') {
      return popupPosition;
    }

    if (!childrenRect) {
      return 'top-center';
    }

    const isTop = childrenRect.pageY <= height / 2;
    const verticalPosition = isTop ? 'bottom' : 'top';
    const isLeft = childrenRect.pageX < width / 3;
    const isRight = childrenRect.pageX > (2 * width) / 3;
    const horizontalPosition = isLeft ? 'right' : isRight ? 'left' : 'center';
    return `${verticalPosition}-${horizontalPosition}` as PopupPosition;
  }, [popupPosition, width, height, childrenRect]);

  React.useEffect(() => {
    if (isVisible) {
      childrenContainerRef.current?.measure(
        (x, y, width, height, pageX, pageY) => {
          setChildrenRect({ x, y, width, height, pageX, pageY });
        }
      );
    }
    // childrenContainerRef is a useRef so we can remove it from the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  return {
    childrenRect,
    finalPopupPosition,
  };
}
